import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { paymentsAPI } from '../services/api';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setMessage('Session de paiement non trouvée');
      return;
    }

    const checkPayment = async () => {
      try {
        const data = await paymentsAPI.getPaymentStatus(sessionId);
        
        if (data.payment_status === 'paid') {
          setStatus('success');
          setMessage('Votre paiement a été confirmé !');
        } else if (data.status === 'expired') {
          setStatus('error');
          setMessage('La session de paiement a expiré');
        } else if (attempts < maxAttempts) {
          // Still processing, poll again
          setTimeout(() => {
            setAttempts(prev => prev + 1);
          }, 2000);
        } else {
          setStatus('error');
          setMessage('Vérification du paiement en cours. Consultez votre email.');
        }
      } catch (error) {
        console.error('Error checking payment:', error);
        if (attempts < maxAttempts) {
          setTimeout(() => {
            setAttempts(prev => prev + 1);
          }, 2000);
        } else {
          setStatus('error');
          setMessage('Erreur lors de la vérification du paiement');
        }
      }
    };

    checkPayment();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-20">
      <Card className="max-w-md w-full bg-white/5 border-white/10">
        <CardContent className="p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-emerald-400 animate-spin mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-white mb-2">
                Vérification du paiement...
              </h1>
              <p className="text-gray-400">
                Merci de patienter quelques instants.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                Paiement réussi !
              </h1>
              <p className="text-gray-400 mb-6">
                {message}
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Vous avez maintenant accès à toutes les fonctionnalités de VALORAMAP.
              </p>
              <Link to="/screener">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
                  Accéder au screener
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                Problème de paiement
              </h1>
              <p className="text-gray-400 mb-6">
                {message}
              </p>
              <div className="flex gap-3 justify-center">
                <Link to="/pricing">
                  <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
                    Retour aux tarifs
                  </Button>
                </Link>
                <Link to="/">
                  <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
                    Accueil
                  </Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
